import { useEffect, useState } from "react";
import { Modal, Button, Form, InputGroup, Card, Spinner } from "react-bootstrap";
import { getKiinteistotWithRakennukset } from "../api/api";
import ConfirmModal from "./ConfirmModal";

function KorttiIdGetter({ show, onClose, onSelect }) {
  const [kortit, setKortit] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  // ✅ Pagination state
  const [page, setPage] = useState(1);
  const [limit] = useState(6);
  const [totalPages, setTotalPages] = useState(1);

  // ✅ Confirmation modal state
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  

  // -----------------------------
  // Fetch kortit whenever modal opens or page changes
  // -----------------------------
  useEffect(() => {
    if (show) fetchKortit();
  }, [show, page]);

  // Reset modal state when closed
  useEffect(() => {
    if (!show) {
      setPage(1);
      setSearchTerm("");
      setKortit([]);
      setSelectedId(null);
      setShowConfirm(false);
    }
  }, [show]);

  // -----------------------------
  // Fetch API
  // -----------------------------
  const fetchKortit = async () => {
    setLoading(true);
    try {
      const response = await getKiinteistotWithRakennukset(
        page,
        limit,
        "id_kiinteisto",
        "DESC",
        searchTerm
      );

      setKortit(response.data.data || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (err) {
      console.error("Failed to fetch kortit:", err);
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // Search handler
  // -----------------------------
  const handleSearch = () => {
    setPage(1); // reset page first
    fetchKortit();
  };

  // -----------------------------
  // User clicked "Valitse" → show confirmation
  // -----------------------------
  const handleChooseClick = (id) => {
    setSelectedId(id);
    setShowConfirm(true);
  };

  // -----------------------------
  // User confirmed selection
  // -----------------------------
  const handleConfirmChoose = () => {
    if (selectedId !== null) {
      onSelect(selectedId); // call parent
    }
    setShowConfirm(false);
    onClose();
    setSelectedId(null);
  };

  // -----------------------------
  // Pagination controls
  // -----------------------------
  const nextPage = () => {
    if (page < totalPages) setPage(page + 1);
  };

  const prevPage = () => {
    if (page > 1) setPage(page - 1);
  };

  return (
    <>
      {/* Kortti modal */}
      <Modal show={show} onHide={onClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Valitse taloyhtiökortti</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Search */}
          <InputGroup className="mb-3">
            <Form.Control
              placeholder="Hae osoitteella tai tunnuksella..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
            <Button variant="success" onClick={handleSearch}>
              Hae
            </Button>
          </InputGroup>

          {/* Loading */}
          {loading && (
            <div className="text-center my-4">
              <Spinner animation="border" />
            </div>
          )}

          {/* Results */}
          {!loading && (
            <div className="row g-3">
              {kortit.map((k) => (
                <div key={k.id_kiinteisto} className="col-md-6">
                  <Card className="shadow-sm rounded-3">
                    <Card.Body>
                      <div className="fw-bold">{k.kohteen_nimi || ""}</div>
                      <div className="fw-bold">
                        {k.rakennukset_fulls?.[0]?.osoite || "Tuntematon osoite"}
                      </div>
                      <div className="text-muted small">
                        {k.rakennukset_fulls?.[0]?.postinumero}{" "}
                        {k.rakennukset_fulls?.[0]?.toimipaikka}
                      </div>

                      <Button
                        variant="outline-success"
                        size="sm"
                        className="mt-2 w-100"
                        onClick={() => handleChooseClick(k.id_kiinteisto)}
                      >
                        Valitse
                      </Button>
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          )}

          {/* Empty */}
          {!loading && kortit.length === 0 && (
            <p className="text-center text-muted mt-3">Ei tuloksia.</p>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-4">
              <Button variant="outline-secondary" disabled={page === 1} onClick={prevPage}>
                ⬅ Edellinen
              </Button>

              <div className="fw-semibold">
                Sivu {page} / {totalPages}
              </div>

              <Button
                variant="outline-secondary"
                disabled={page === totalPages}
                onClick={nextPage}
              >
                Seuraava ➡
              </Button>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Peruuta
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ✅ Confirmation modal */}
      <ConfirmModal
        show={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleConfirmChoose}
        title="Vahvista valinta"
        message="Haluatko varmasti valita tämän kortin?"
        confirmText="Kyllä"
        cancelText="Ei"
        confirmVariant="success"
      />
    </>
  );
}

export default KorttiIdGetter;
